"use client";

import { NEARBY_SUPS_QUERY } from "@/gql/nearby-sups";
import { type NearbyProperty } from "@/types/sup";
import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";

interface UseNearbySUPsParams {
	coordinates: { lat: number; lng: number };
	type: "unit" | "sp";
	unitId?: string; // Optional: enables ranked/filtered results
	initialData?: NearbyProperty[];
	initialError?: string | null;
	excludeId?: string;
}

interface UseNearbySUPsReturn {
	data: NearbyProperty[] | null;
	error: string | null;
	hasMore: boolean;
	hasPrevious: boolean;
	loadingMore: boolean;
	loadingPrevious: boolean;
	loadMore: () => Promise<void>;
	loadPrevious: () => Promise<void>;
	retry: () => void;
	searchAfter: string | null;
	searchBefore: string | null;
}

export function useNearbySUPs({
	coordinates,
	type,
	unitId,
	initialData,
	initialError,
	excludeId,
}: UseNearbySUPsParams): UseNearbySUPsReturn {
	const [data, setData] = useState<NearbyProperty[] | null>(initialData || null);
	const [error, setError] = useState<string | null>(initialError || null);
	const [hasMore, setHasMore] = useState(true);
	const [hasPrevious, setHasPrevious] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [loadingPrevious, setLoadingPrevious] = useState(false);
	const [searchAfter, setSearchAfter] = useState<string | null>(null);
	const [searchBefore, setSearchBefore] = useState<string | null>(null);
	const lastCoordinatesRef = useRef<string>("");
	const lastTypeRef = useRef<string>("");

	const [fetchSUPs] = useLazyQuery(NEARBY_SUPS_QUERY, {
		errorPolicy: "all",
		fetchPolicy: "cache-and-network",
	});

	const loadMore = useCallback(async () => {
		if (loadingMore || !hasMore) return;

		setLoadingMore(true);
		setError(null);

		try {
			const { data: result, error: apolloError } = await fetchSUPs({
				variables: {
					latitude: coordinates.lat.toString(),
					longitude: coordinates.lng.toString(),
					type,
					unitId, // Optional - enables ranking when provided
					limit: 20,
					searchAfter,
					searchBefore: null,
					excludeId,
				},
			});

			if (apolloError) {
				setError(apolloError.message);
				return;
			}

			const newData = result?.sellerUnitsAndProjects?.getNearby;
			if (newData?.data) {
				setData(prev => [...(prev || []), ...newData.data]);
				setSearchAfter(newData.searchAfter);
				setHasMore(newData.hasMore || false);
				setHasPrevious(true);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to load more");
		} finally {
			setLoadingMore(false);
		}
	}, [coordinates, type, unitId, searchAfter, hasMore, loadingMore, fetchSUPs, excludeId]);

	const loadPrevious = useCallback(async () => {
		if (loadingPrevious || !hasPrevious) return;

		setLoadingPrevious(true);
		setError(null);

		try {
			const { data: result, error: apolloError } = await fetchSUPs({
				variables: {
					latitude: coordinates.lat.toString(),
					longitude: coordinates.lng.toString(),
					type,
					unitId, // Optional - enables ranking when provided
					limit: 20,
					searchAfter: null,
					searchBefore,
					excludeId,
				},
			});

			if (apolloError) {
				setError(apolloError.message);
				return;
			}

			const newData = result?.sellerUnitsAndProjects?.getNearby;
			if (newData?.data) {
				setData(prev => [...newData.data, ...(prev || [])]);
				setSearchBefore(newData.searchBefore);
				setHasPrevious(newData.hasMore || false);
				setHasMore(true);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to load previous");
		} finally {
			setLoadingPrevious(false);
		}
	}, [coordinates, type, unitId, searchBefore, hasPrevious, loadingPrevious, fetchSUPs, excludeId]);

	const retry = useCallback(() => {
		setError(null);
		setData(null);
		setHasMore(true);
		setHasPrevious(false);
		setSearchAfter(null);
		setSearchBefore(null);
		loadMore();
	}, [loadMore]);

	// Initial data fetch on component mount if no initial data is provided
	useEffect(() => {
		// Only fetch on initial mount if no initial data is provided
		if ((!initialData || initialData.length === 0) && !data) {
			loadMore();
		}
	}, [initialData, data, loadMore]); // Include dependencies to ensure proper fetching

	// Reset state when coordinates or type changes and automatically fetch data if no initial data
	useEffect(() => {
		const currentCoordinates = `${coordinates.lat},${coordinates.lng}`;
		const currentType = type;

		if (lastCoordinatesRef.current !== currentCoordinates || lastTypeRef.current !== currentType) {
			setData(initialData || null);
			setError(initialError || null);
			setHasMore(true);
			setHasPrevious(false);
			setSearchAfter(null);
			setSearchBefore(null);

			lastCoordinatesRef.current = currentCoordinates;
			lastTypeRef.current = currentType;

			// Automatically fetch data if no initial data is provided
			if (!initialData || initialData.length === 0) {
				loadMore();
			}
		}
	}, [coordinates.lat, coordinates.lng, type, initialData, initialError, loadMore]);

	return {
		data,
		error,
		hasMore,
		hasPrevious,
		loadingMore,
		loadingPrevious,
		loadMore,
		loadPrevious,
		retry,
		searchAfter,
		searchBefore,
	};
}
